import {
  __,
  complement,
  identity,
  isEmpty,
  compose,
  path,
  ifElse,
  either,
  propEq,
  prop,
  both,
  isNil,
  concat,
  toUpper,
  head,
  tail,
  map,
  values,
  curry,
  reduce,
  converge,
  pathSatisfies,
  always,
  cond,
  is,
  join,
  pipe,
  propOr,
  propSatisfies,
  filter,
  divide,
  subtract,
  add,
  keys,
  gt,
  or,
  T,
} from 'ramda'
import moment from 'moment'
import * as SurveyConstants from 'utils/constants/survey'
import { timezone } from './user'

export const toggle = complement(identity)
export const preventDefault = e => e.preventDefault()
export const notEmpty = complement(isEmpty)
export const notNil = complement(isNil)
export const notEmptyOrNil = both(notEmpty, notNil)
export const onInput = f => compose(f, path(['currentTarget', 'value']))
export const isEnter = either(propEq('which', 13), propEq('keyCode', 13))
export const isShiftKey = propEq('shiftKey', true)
export const isShiftEnter = both(isShiftKey, isEnter)
export const isEnterWithoutShift = both(complement(isShiftKey), isEnter)
export const noop = () => {}
export const ifKey = keyPredicate => f => ifElse(
  keyPredicate,
    compose(f, preventDefault),
  noop
)
export const hasLength = pathSatisfies(l => l > 0, ['length'])
export const ifEnter = ifKey(isEnter)
export const ifEnterWithoutShift = ifKey(isEnterWithoutShift)

export const isEmptyOrNil = either(isEmpty, isNil)
export const fieldDoesNotHaveError = compose(isEmptyOrNil, prop('error'))
export const onChange = (f, type = 'value') => compose(f, path(['currentTarget', type]))

export const fromArray = reduce((obj, x) => Object.assign(obj, { [x]: x }), {})

export const makeGUID = () => {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1) // eslint-disable-line no-bitwise, max-len
  return (`${S4() + S4()}-${S4()}-4${S4().substr(0, 3)}-${S4()}-${S4()}${S4()}${S4()}`).toLowerCase()
}

export const withObjectId = object => ({
  ...object,
  _objectId: makeGUID(),
})

// TODO - merge, this is really just an addition to "onInput" but don't want to cause a regression
// this is a bit of a hack to move cursor to end of input when autofocusing
export const moveCursorToEnd = e => {
  const tmp = e.target.value
  e.target.value = ''
  e.target.value = tmp
}

export const capitalizeFirstLetter = converge(concat, [compose(toUpper, head), tail])

export const truncateString = (string, numCharactersAtStart, numCharactersAtEnd) =>
  string.replace(new RegExp(`^(.{${numCharactersAtStart}})(.{3,})(.{${numCharactersAtEnd}})$`), '$1...$3')

export const mapValues = curry((f, object) => compose(
  map(f),
  values
)(object))

export const propIsEmptyOrNil = p => compose(isEmptyOrNil, prop(p))

export const makeReducer = (handlers, defaultState = {}) => (state = defaultState, action = {}) => {
  const handler = path([action.type], handlers)
  return handler ? handler(state, action) : state
}

const numericMinIsNumber = pipe(propOr(null, 'numericMin'), is(Number))
const numericMaxIsNumber = pipe(propOr(null, 'numericMax'), is(Number))
const formatMinMax = ({numericMin, numericMax}) => `${numericMin} to ${numericMax}`
const formatMin = ({numericMin}) => `Greater than or equal to ${numericMin}`
const formatMax = ({numericMax}) => `Less than or equal to ${numericMax}`

export const formatCrowdAttributeValues = (crowdAttribute, prettyPrintChar = ', ') =>
  cond([
    [both(numericMinIsNumber, numericMaxIsNumber), formatMinMax],
    [numericMinIsNumber, formatMin],
    [numericMaxIsNumber, formatMax],
    [T, pipe(propOr([], 'choices'), map(prop('label')), join(prettyPrintChar))],
  ])(crowdAttribute)

const getMarketSpecificLabel = path(['marketInfo', 'label'])
const getAttributeName = path(['attribute', 'name'])

export const formatCrowdAttributeLabel = cond([
  [getMarketSpecificLabel, getMarketSpecificLabel],
  [getAttributeName, getAttributeName],
  [T, always('')],
])

export const convertToHtml = (text, readonly = false) => {
  if (readonly) {
    return text.replace(/{{(.*?)}}/gm, '<span class="placeholder">$1</span>')
  }
  return text.replace(/{{(.*?)}}/gm, '<span class="placeholder" contentEditable="true">$1</span>')
}
 
export const inField = either(propEq('status', SurveyConstants.FIELDING), propEq('status', SurveyConstants.REFIELDING))
export const closed = either(propEq('status', SurveyConstants.AUTHORING), propEq('status', SurveyConstants.CLOSED))
export const deepSetTrue = ifElse(is(Object), ifElse(prop('choiceIds'), T, map(o => deepSetTrue(o))), T)
export const formatAvailability = (availability, timeZone) => `${moment.tz(availability.startTime, timeZone).locale('en').format('dddd, MMMM Do YYYY, h:mm A')} - ${moment.tz(availability.endTime, timeZone).locale('en').format('hh:mm A')}`
export const half = divide(__, 2)
export const isSchedulerQuestion = complement(propEq('typeId', 13))
export const stripHTML = str => str.replace(/<\/?[^>]+>/g, '')
export const surveysInField = filter(propSatisfies(inField, 'status'))
export const surveysPaused = filter(propSatisfies(closed, 'status'))


export const lessThan24Hours = (time) => {
  const TWENTY_FOUR_HOURS = 60 * 60 * 1000 * 24
  return Math.abs(new Date(time) - (new Date)) < TWENTY_FOUR_HOURS
}

export const moreCompletionsNeededThanAvailableSlots = (
  surveys = [],
  numAvailableSlots,
  rescheduling = false,
  chosenSlots = {},
) => {
  const totalCompletionsNeeded = reduce(
    (totalCompletesNeeded, survey) => pipe(
      subtract(survey.completionsNeeded),
      add(totalCompletesNeeded)
    )(survey.numCompletions),
    0, surveys
  )

  const slotsBeingRescheduled = keys(chosenSlots).length

  return rescheduling ?
    gt(totalCompletionsNeeded + or(slotsBeingRescheduled, 1), numAvailableSlots) :
    gt(totalCompletionsNeeded, numAvailableSlots)
}
 
export function parseJwt(token) {
  let base64Url = token.split('.')[1]; // token you get
  let base64 = base64Url.replace('-', '+').replace('_', '/');
  let decodedData = JSON.parse(Buffer.from(base64, 'base64').toString('binary'));
  return decodedData
}

export function inject(str, obj) { return str.replace(/\${(.*?)}/g, (x, g) => obj[g]) }

export const convert12to24Hours = timeStr => {
   const [time, modifier] = timeStr.split(' ');
   let [hours, minutes] = time.split(':');
   if (hours === '12') {
      hours = '00';
   }
   if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12;
   }
   return `${hours}${minutes}`;
};

export function convert24to12Hours (time) {
  // Check correct time format and split into components
  if (time.toString().length === 3) {
    time = '0' +time
  } else if (time.toString().length === 2) {
    time = '00' +time
  } else if (time.toString().length === 1) {
    time = '000' +time
  }
  time = time.toString ().match (/^([01]\d|2[0-3])([0-5]\d)(:[0-5]\d)?$/) || [time];

  if (time.length > 1) { // If time format correct
    time = time.slice (1);  // Remove full string match value
    time[2] = +time[0] < 12 ? ' AM' : ' PM'; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
    if (time[0]  < 10) {
      time[0]  = '0' + time[0]+':';
    } else { 
      time[0]  = time[0]+':';
    }
  }
  return time.join ('');
}


export const toHoursAndMinutes = (totalMinutes) => {
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
}

 const padTo2Digits = (num) => {
  return num.toString().padStart(2, '0');
}

export const fillUserTimezone = (userTimezone) => {
    if (userTimezone) {
      return { timeZone: userTimezone }
    } else if (timezone.get(moment.tz.guess())) {
      return { timeZone: moment.tz.guess() }
    } else {
      return { timeZone: "America/New_York", }
    }
}

export const fillTimezoneLabel = (timezone) => {
  const offset = moment.tz(timezone).utcOffset() >0 ? "+" : "-"
  return  "(UTC"+ offset +toHoursAndMinutes(Math.abs(moment.tz(timezone).utcOffset()))+")"
}

export const addTranslationsLabel = (defaultLocalizationMap, key, value) => {
  let label = ''
  defaultLocalizationMap.get(key) ?
    label = value.language_name + "-" + value.country + " (" + value.language_code + "-" + value.country_code.toUpperCase() + ")"
    :
    label = value.language_name + " (" + value.language_code + ")"
  return label
}


export const addTopicNameTranslationsLabel = (languageAndLocalizationMap, defaultLocalizationArray, key) => {
  let label = ''
  defaultLocalizationArray.get(key) ?
    label =  `${defaultLocalizationArray.get(key).language_name}-${defaultLocalizationArray.get(key).country} (${defaultLocalizationArray.get(key).language_code}-${defaultLocalizationArray.get(key).country_code.toUpperCase()})`
    :
    label = `${languageAndLocalizationMap.get(key).language_name} (${languageAndLocalizationMap.get(key).language_code})`
  return label
}

export const sortAlphabetically = (chosenTranslations) =>
  chosenTranslations.sort((a, b) => {
    let fa = a.language_name.toLowerCase(),
      fb = b.language_name.toLowerCase(),
      ca = a.country && a.country.toLowerCase(),
      cb = b.country && b.country.toLowerCase();
    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    if(fa === fb && ca > cb) {
      return 1;
    }
    if(fa === fb && ca < cb) {
      return -1;
    }
    return 0;
  });


  export const showOsTerminationPages = () =>{ return false }
  